import { Divider, ListItemIcon, ListItemText, Menu, MenuItem, MenuProps } from "@mui/material";
import { MdsAddCircle, MdsArrowSplit, MdsCopyAll, MdsDelete, MdsPageHeader } from "@sinch/icons";
import { isNilOrEmpty } from "ramda-adjunct";
import React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MdsIcon } from "@sinch/components";
import { useRateTreeContext } from "@sinch/components/application/RatelistBuilder/BuilderTree/RateBuilderTreeProvider";
import { useAddTreeNode } from "@sinch/components/application/RatelistBuilder/hooks/UseAddTreeNode";
import { RateBuilderTreeFormFields } from "@sinch/components/application/RatelistBuilder/ratelistBuilderSchema";
import { RateBuilderTreeViewItem } from "@sinch/components/application/RatelistBuilder/types";
import { useRateBuilderSettings } from "@sinch/components/application/RatelistBuilder/UtilitiesProviders/RateBuilderSettingsProvider";

/**
 * Context menu for tree rate builder items with options to add nested rates,
 * add else nodes, delete, duplicate, and add names to rates.
 */
export interface RateBuilderContextMenuProps extends MenuProps {
  /** Close menu callback */
  onClose: () => void;
  /** Current row ID */
  rowId: string;
}

export const RateBuilderContextMenu: React.FC<RateBuilderContextMenuProps> = ({ onClose, rowId, ...props }) => {
  const { t } = useTranslation();
  const { remove, copy, getParent } = useRateTreeContext<RateBuilderTreeViewItem>();
  const { setValue } = useFormContext<RateBuilderTreeFormFields>();
  const name = useWatch<RateBuilderTreeFormFields>({ name: `Rate.${rowId}.name` });
  const { disableNesting } = useRateBuilderSettings();
  const { addCondition, addElseNode } = useAddTreeNode(rowId);

  return (
    <Menu {...props}>
      {!disableNesting && (
        <>
          <MenuItem
            data-cy="RateBuilder-add-nested"
            onClick={() => {
              addCondition();
              onClose();
            }}
          >
            <ListItemIcon>
              <MdsIcon fontSize="small" icon={MdsAddCircle} />
            </ListItemIcon>
            <ListItemText>{t("RateBuilder.addNestedRate")}</ListItemText>
          </MenuItem>
          <MenuItem
            data-cy="RateBuilder-add-else-node"
            onClick={() => {
              addElseNode();
              onClose();
            }}
          >
            <ListItemIcon>
              <MdsIcon fontSize="small" icon={MdsArrowSplit} />
            </ListItemIcon>
            <ListItemText>{t("RateBuilder.addElseNode")}</ListItemText>
          </MenuItem>
          <Divider />
        </>
      )}
      <MenuItem
        data-cy="RateBuilder-delete-node"
        onClick={() => {
          const parent = getParent(rowId);
          remove(rowId);
          if (parent?.type === "wrapper" && parent?.children?.length === 1) {
            remove(parent.id);
          }
          onClose();
        }}
      >
        <ListItemIcon>
          <MdsIcon fontSize="small" icon={MdsDelete} />
        </ListItemIcon>
        <ListItemText>{t("delete")}</ListItemText>
      </MenuItem>
      <MenuItem
        data-cy="RateBuilder-copy-node"
        onClick={() => {
          copy(rowId);
          onClose();
        }}
      >
        <ListItemIcon>
          <MdsIcon fontSize="small" icon={MdsCopyAll} />
        </ListItemIcon>
        <ListItemText>{t("Action.copy")}</ListItemText>
      </MenuItem>
      {isNilOrEmpty(name) && (
        <MenuItem
          onClick={() => {
            setValue(`Rate.${rowId}.name`, `New rate`);
            onClose();
          }}
        >
          <ListItemIcon>
            <MdsIcon fontSize="small" icon={MdsPageHeader} />
          </ListItemIcon>
          <ListItemText>{t("RateBuilder.addName")}</ListItemText>
        </MenuItem>
      )}
    </Menu>
  );
};
